// ============================================
// HLX-PRO.04 — Pre-Filter Engine Service
//
// Pass 1 of Two-Pass Safety Validation.
// Deterministic gate that removes unsafe products
// BEFORE the LLM sees them.
//
// Silver MVP scope:
// 1. Block-severity rule evaluation (D-1)
// 2. Gender filtering
// 3. Age filtering (D-1)
// 4. Conservative defaults (D-4)
// 5. FilteredFormulary return type (D-3)
// 6. Empty formulary detection
//
// LOCKED CONSTRAINTS:
// - Pre-filter is a GATE, not a suggestion
// - LLM cannot recommend what it cannot see
// - Empty formulary → hard stop
// - Conservative defaults on missing data
// ============================================

import type { SafetyRule } from '../../types/formulary';
import type {
  PreFilterInput,
  FilteredFormulary,
  PreFilterExclusion,
  ConservativeDefault,
} from '../../types/preFilter';

import { COMMON_ALLERGEN_KEYWORDS } from '../../types/inputAssembly';

/** PRO.04 #6: Pre-filter engine version for traceability */
export const PRE_FILTER_VERSION = '1.0.0';

// ── Rule Evaluation ──

/**
 * Check if a patient medication matches a rule's matchMedications.
 * Case-insensitive substring match.
 */
export function matchesMedication(
  patientMeds: string[],
  ruleMeds: string[] | undefined,
): boolean {
  if (!ruleMeds || ruleMeds.length === 0) return false;
  const normalizedPatient = patientMeds.map((m) => m.toLowerCase().trim());
  return ruleMeds.some((ruleMed) => {
    const normalized = ruleMed.toLowerCase().trim();
    return normalizedPatient.some((pm) => pm.includes(normalized) || normalized.includes(pm));
  });
}

/**
 * Check if a patient condition matches a rule's matchConditions.
 */
export function matchesCondition(
  patientConditions: string[],
  ruleConditions: string[] | undefined,
): boolean {
  if (!ruleConditions || ruleConditions.length === 0) return false;
  const normalized = patientConditions.map((c) => c.toLowerCase().trim());
  return ruleConditions.some((rc) => {
    const norm = rc.toLowerCase().trim();
    return normalized.some((pc) => pc.includes(norm) || norm.includes(pc));
  });
}

/**
 * Check if a patient allergen matches a rule's matchAllergens.
 */
export function matchesAllergen(
  patientAllergies: string[],
  ruleAllergens: string[] | undefined,
): boolean {
  if (!ruleAllergens || ruleAllergens.length === 0) return false;
  const normalized = patientAllergies.map((a) => a.toLowerCase().trim());
  return ruleAllergens.some((ra) => {
    const norm = ra.toLowerCase().trim();
    return normalized.some((pa) => pa.includes(norm) || norm.includes(pa));
  });
}

/**
 * Check if pregnancy rules should fire.
 * PRO.04 #3: Also handles pregnancyUnknown conservative default —
 * if isPregnant is null/undefined and pregnancyConservativeDefault is active,
 * the product is blocked by filterByPregnancyCategory, not here.
 * This function handles the explicit isPregnant === true case only.
 */
export function matchesPregnancy(
  isPregnant: boolean | undefined | null,
  rule: SafetyRule,
): boolean {
  if (rule.triggerType !== 'pregnancy') return false;
  return isPregnant === true;
}

// ── Product Filtering ──

/**
 * Evaluate all block-severity rules against a product.
 * Returns exclusion if any rule blocks this product.
 */
export function evaluateRulesForProduct(
  productId: string,
  productName: string,
  rules: SafetyRule[],
  input: PreFilterInput,
): PreFilterExclusion | null {
  const blockRules = rules.filter((r) => r.severity === 'block' && r.isActive);

  for (const rule of blockRules) {
    const affectsProduct =
      rule.affectedProductIds.includes(productId) ||
      (rule.matchProductIds && rule.matchProductIds.includes(productId));

    if (!affectsProduct) continue;

    // Check each trigger type
    if (rule.triggerType === 'drug_interaction' && matchesMedication(input.medications, rule.matchMedications)) {
      return {
        productId,
        productName,
        ruleId: rule.id,
        ruleName: rule.name,
        triggerType: 'drug_interaction',
        reason: `Blocked: ${rule.message}`,
        patientDataSource: 'medication',
      };
    }

    if (rule.triggerType === 'condition' && matchesCondition(input.conditions, rule.matchConditions)) {
      return {
        productId,
        productName,
        ruleId: rule.id,
        ruleName: rule.name,
        triggerType: 'condition',
        reason: `Blocked: ${rule.message}`,
        patientDataSource: 'condition',
      };
    }

    if (rule.triggerType === 'allergy' && matchesAllergen(input.allergies, rule.matchAllergens)) {
      return {
        productId,
        productName,
        ruleId: rule.id,
        ruleName: rule.name,
        triggerType: 'allergy',
        reason: `Blocked: ${rule.message}`,
        patientDataSource: 'allergy',
      };
    }

    if (rule.triggerType === 'pregnancy' && matchesPregnancy(input.isPregnant, rule)) {
      return {
        productId,
        productName,
        ruleId: rule.id,
        ruleName: rule.name,
        triggerType: 'pregnancy',
        reason: `Blocked: ${rule.message}`,
        patientDataSource: 'pregnancy',
      };
    }
  }

  return null;
}

/**
 * Filter product by gender.
 * Returns exclusion if product doesn't match patient sex.
 */
export function filterByGender(
  product: { id: string; name: string; gender?: string },
  patientSex: 'male' | 'female' | 'unknown' | undefined,
  sexProvided: boolean,
): PreFilterExclusion | null {
  const productGender = product.gender;
  if (!productGender || productGender === 'any') return null;

  // Sex unknown → block ALL gender-specific products
  // PRO.04 #4: Also handles 'unknown' sex from PRO.01
  if (!sexProvided || !patientSex || patientSex === 'unknown') {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'GENDER-UNKNOWN',
      ruleName: 'Gender-specific product (sex unknown)',
      triggerType: 'gender',
      reason: `Blocked: gender-specific product (${productGender}) — patient sex unknown`,
      patientDataSource: 'gender',
    };
  }

  // Gender mismatch
  if (productGender !== patientSex) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'GENDER-MISMATCH',
      ruleName: 'Gender mismatch',
      triggerType: 'gender',
      reason: `Blocked: ${productGender}-only product for ${patientSex} patient`,
      patientDataSource: 'gender',
    };
  }

  return null;
}

/**
 * Filter product by age constraints.
 */
export function filterByAge(
  product: { id: string; name: string; ageMin?: number; ageMax?: number },
  patientAge: number | undefined,
  ageProvided: boolean,
): PreFilterExclusion | null {
  const hasAgeConstraint =
    (product.ageMin !== undefined && product.ageMin !== null) ||
    (product.ageMax !== undefined && product.ageMax !== null);

  if (!hasAgeConstraint) return null;

  // Age unknown → block all age-constrained products
  if (!ageProvided || patientAge === undefined) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'AGE-UNKNOWN',
      ruleName: 'Age-constrained product (age unknown)',
      triggerType: 'age',
      reason: `Blocked: age-constrained product — patient age unknown`,
      patientDataSource: 'age',
    };
  }

  if (product.ageMin !== undefined && patientAge < product.ageMin) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'AGE-MIN',
      ruleName: 'Below minimum age',
      triggerType: 'age',
      reason: `Blocked: requires age ${product.ageMin}+ (patient is ${patientAge})`,
      patientDataSource: 'age',
    };
  }

  if (product.ageMax !== undefined && patientAge > product.ageMax) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'AGE-MAX',
      ruleName: 'Above maximum age',
      triggerType: 'age',
      reason: `Blocked: max age ${product.ageMax} (patient is ${patientAge})`,
      patientDataSource: 'age',
    };
  }

  return null;
}

/**
 * Defense-in-depth: check pregnancyCategory on product.
 */
export function filterByPregnancyCategory(
  product: { id: string; name: string; pregnancyCategory?: string },
  isPregnant: boolean | undefined,
  pregnancyUnknown: boolean,
): PreFilterExclusion | null {
  if (!product.pregnancyCategory || product.pregnancyCategory !== 'X') return null;

  if (isPregnant === true || pregnancyUnknown) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'PREGNANCY-CAT-X',
      ruleName: 'Category X (pregnancy)',
      triggerType: 'pregnancy',
      reason: `Blocked: Category X product — contraindicated in pregnancy`,
      patientDataSource: 'pregnancy',
    };
  }

  return null;
}

/**
 * Block common allergen products when allergies are unknown.
 * Conservative default: if patient hasn't confirmed allergies,
 * block products matching COMMON_ALLERGEN_KEYWORDS.
 */
export function filterByAllergenConservativeDefault(
  product: { id: string; name: string },
  allergiesProvided: boolean,
): PreFilterExclusion | null {
  if (allergiesProvided) return null;

  const productNameLower = product.name.toLowerCase();
  const matchedAllergen = COMMON_ALLERGEN_KEYWORDS.find(
    (keyword) => productNameLower.includes(keyword.toLowerCase()),
  );

  if (matchedAllergen) {
    return {
      productId: product.id,
      productName: product.name,
      ruleId: 'ALLERGEN-UNKNOWN',
      ruleName: `Common allergen product (allergies unknown): ${matchedAllergen}`,
      triggerType: 'allergy',
      reason: `Blocked: product contains common allergen '${matchedAllergen}' — patient allergies unknown`,
      patientDataSource: 'allergy',
    };
  }

  return null;
}

// ── Conservative Defaults ──

/**
 * Evaluate conservative defaults for missing data.
 */
export function evaluateConservativeDefaultsForFilter(
  input: PreFilterInput,
): { pregnancyUnknown: boolean; defaults: ConservativeDefault[] } {
  const defaults: ConservativeDefault[] = [];
  let pregnancyUnknown = false;

  const dc = input.dataCompleteness;

  if (dc && !dc.sexProvided) {
    defaults.push({
      field: 'sex',
      assumption: 'Sex unknown — blocking gender-specific products both directions',
      productsBlocked: 0, // Will be counted during filtering
    });
  }

  if (dc && !dc.pregnancyStatusProvided && (input.sex === 'female' || !dc.sexProvided)) {
    pregnancyUnknown = true;
    defaults.push({
      field: 'pregnancy',
      assumption: 'Pregnancy unknown (female or sex unknown) — blocking Category X products',
      productsBlocked: 0,
    });
  }

  if (dc && !dc.allergiesProvided) {
    defaults.push({
      field: 'allergies',
      assumption: 'Allergies unknown — blocking common allergen products',
      productsBlocked: 0,
    });
  }

  if (dc && !dc.ageProvided) {
    defaults.push({
      field: 'age',
      assumption: 'Age unknown — blocking age-constrained products',
      productsBlocked: 0,
    });
  }

  return { pregnancyUnknown, defaults };
}

// ── Main Pre-Filter ──

/**
 * Run the pre-filter engine.
 *
 * LOCKED: The LLM cannot recommend what it cannot see.
 * LOCKED: Empty formulary → hard stop, no generation.
 */
export function runPreFilter(
  input: PreFilterInput,
  rules: SafetyRule[],
): FilteredFormulary {
  const exclusions: PreFilterExclusion[] = [];
  const blockedIds = new Set<string>();
  const totalBefore = input.products.length;

  const dc = input.dataCompleteness;
  const sexProvided = dc?.sexProvided ?? (input.sex !== undefined);
  const ageProvided = dc?.ageProvided ?? (input.age !== undefined);

  const { pregnancyUnknown, defaults } = evaluateConservativeDefaultsForFilter(input);

  // Evaluate each product
  for (const product of input.products) {
    if (blockedIds.has(product.id)) continue;

    // 1. Rule-based filtering (block severity only)
    const ruleExclusion = evaluateRulesForProduct(
      product.id,
      product.name,
      rules,
      input,
    );
    if (ruleExclusion) {
      exclusions.push(ruleExclusion);
      blockedIds.add(product.id);
      continue;
    }

    // 2. Gender filtering
    const genderExclusion = filterByGender(product, input.sex, sexProvided);
    if (genderExclusion) {
      exclusions.push(genderExclusion);
      blockedIds.add(product.id);
      continue;
    }

    // 3. Age filtering
    const ageExclusion = filterByAge(product, input.age, ageProvided);
    if (ageExclusion) {
      exclusions.push(ageExclusion);
      blockedIds.add(product.id);
      continue;
    }

    // 4. Pregnancy Category X defense-in-depth
    const pregnancyExclusion = filterByPregnancyCategory(
      product,
      input.isPregnant,
      pregnancyUnknown,
    );
    if (pregnancyExclusion) {
      exclusions.push(pregnancyExclusion);
      blockedIds.add(product.id);
      continue;
    }

    // 5. Allergen conservative default (allergies unknown → block common allergen products)
    const allergenExclusion = filterByAllergenConservativeDefault(
      product,
      dc?.allergiesProvided ?? true,
    );
    if (allergenExclusion) {
      exclusions.push(allergenExclusion);
      blockedIds.add(product.id);
      continue;
    }
  }

  // Update conservative default blocked counts
  // PRO.04 #2: Include both conservative-default AND rule-based exclusions for each field
  for (const d of defaults) {
    d.productsBlocked = exclusions.filter((e) => {
      if (d.field === 'sex') return e.ruleId === 'GENDER-UNKNOWN';
      if (d.field === 'pregnancy') return e.ruleId === 'PREGNANCY-CAT-X';
      if (d.field === 'age') return e.ruleId === 'AGE-UNKNOWN';
      if (d.field === 'allergies') return e.ruleId === 'ALLERGEN-UNKNOWN' || e.triggerType === 'allergy';
      return false;
    }).length;
  }

  const survivingProducts = input.products.filter((p) => !blockedIds.has(p.id));

  return {
    products: survivingProducts,
    isEmpty: survivingProducts.length === 0,
    totalBeforeFilter: totalBefore,
    totalAfterFilter: survivingProducts.length,
    exclusions,
    filteredAt: new Date().toISOString(),
    rulesSnapshotId: input.rulesSnapshotId,
    conservativeDefaults: defaults,
    preFilterVersion: PRE_FILTER_VERSION,
  };
}
